import { useEffect, useRef } from "react";

export type UseForegroundResumeArgs = {
  /** Device state broadcast function from useDeviceStateBroadcast */
  broadcast: () => void;
  /** Only active when the Chime session is established */
  isEnabled?: boolean;
};

/**
 * Re-broadcasts device state when the tab returns to foreground.
 *
 * Chrome throttles setInterval to 1/min in background tabs, and iOS Safari
 * can suspend JS entirely. The device state heartbeat (15s interval) stops,
 * causing remote participants' intent data to go stale after 30s. On
 * foreground return, this hook fires an immediate broadcast so remote
 * participants have fresh camera/mic intent.
 *
 * Heartbeat recovery is handled separately by useVideoMeetingHeartbeat
 * (resumeOnForeground flag). Tile state recovery (health check, frame
 * capture) self-heals via their own intervals within ~9s.
 */
export function useForegroundResume({
  broadcast,
  isEnabled = true,
}: UseForegroundResumeArgs): void {
  const broadcastRef = useRef(broadcast);
  useEffect(() => {
    broadcastRef.current = broadcast;
  }, [broadcast]);

  useEffect(() => {
    if (!isEnabled) return;

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        broadcastRef.current();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [isEnabled]);
}
